import React, { useState, useEffect } from 'react';
import FormSelect from './FormSelect';
import apiService from '../services/api';
import { useTenant } from '../contexts/TenantContext';

/**
 * BranchSelector - Dropdown for selecting a branch/location
 *
 * Automatically fetches branches for the current club and displays them in a dropdown.
 * Can be used in registration, admin forms, and public pages for filtering.
 * Supports auto-population from URL parameters (e.g., ?branch_id=xyz).
 *
 * @param {string} value - Selected branch ID
 * @param {function} onChange - Callback when selection changes
 * @param {boolean} required - Whether selection is required
 * @param {boolean} disabled - Whether dropdown is disabled
 * @param {string} placeholder - Placeholder text
 * @param {string} label - Label for the dropdown
 * @param {string} error - Error message to display
 * @param {string} helpText - Help text to display
 * @param {boolean} autoPopulateFromUrl - Whether to auto-populate from URL params
 */
const BranchSelector = ({
    value,
    onChange,
    required = false,
    disabled = false,
    placeholder = 'Select a location...',
    label = 'Location',
    error = null,
    helpText = null,
    name = 'branch_id',
    autoPopulateFromUrl = false,
    ...props
}) => {
    const { tenant } = useTenant();
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);

    useEffect(() => {
        const fetchBranches = async () => {
            if (!tenant?.club_id) {
                console.log('⚠️ BranchSelector: No tenant context available');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setFetchError(null);

                console.log('🔍 BranchSelector: Fetching branches for club:', tenant.club_id);

                // Fetch branches from public endpoint
                const response = await apiService.get('/api/public/branches');

                if (response.success) {
                    const branchList = response.data || [];
                    console.log(`✅ BranchSelector: Fetched ${branchList.length} branches`);
                    setBranches(branchList);

                    // Auto-populate from URL if enabled and no value is set
                    if (autoPopulateFromUrl && !value && branchList.length > 0) {
                        const urlParams = new URLSearchParams(window.location.search);
                        const branchIdFromUrl = urlParams.get('branch_id') || urlParams.get('branchId');

                        if (branchIdFromUrl) {
                            // Verify branch exists in the list
                            const branchExists = branchList.some(b => b.id === branchIdFromUrl);
                            if (branchExists) {
                                console.log('✅ BranchSelector: Auto-populating from URL:', branchIdFromUrl);
                                // Trigger onChange to set the value in parent component
                                onChange({ target: { value: branchIdFromUrl, name } });
                            } else {
                                console.warn('⚠️ BranchSelector: Branch from URL not found:', branchIdFromUrl);
                            }
                        }
                    }
                } else {
                    setFetchError('Failed to load locations');
                }
            } catch (err) {
                console.error('❌ BranchSelector: Error fetching branches:', err);
                setFetchError('Failed to load locations');
            } finally {
                setLoading(false);
            }
        };

        fetchBranches();
    }, [tenant?.club_id]); // Removed onChange, value, name, autoPopulateFromUrl from deps

    // Convert branches to options format
    const options = branches.map(branch => ({
        value: branch.id,
        label: `${branch.name}${branch.city ? ` - ${branch.city}` : ''}${branch.state ? `, ${branch.state}` : ''}`
    }));

    // Show loading state
    if (loading) {
        return (
            <FormSelect
                label={label}
                name={name}
                value=""
                onChange={() => {}}
                disabled={true}
                placeholder="Loading locations..."
                {...props}
            />
        );
    }

    // Show error state
    if (fetchError) {
        return (
            <FormSelect
                label={label}
                name={name}
                value=""
                onChange={() => {}}
                disabled={true}
                error={fetchError}
                {...props}
            />
        );
    }

    // Show selector
    return (
        <FormSelect
            label={label}
            name={name}
            value={value}
            onChange={onChange}
            options={options}
            required={required}
            disabled={disabled || branches.length === 0}
            placeholder={branches.length === 0 ? 'No locations available' : placeholder}
            error={error}
            helpText={helpText}
            {...props}
        />
    );
};

export default BranchSelector;
